import React, { useEffect } from "react";
import SimpleTable from "../../table/table";
import { Container, Box, Switch, FormControlLabel } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ViewHeader from "../../viewHeader/viewHeader";
import FiltersHeader from "../../filtersHeader/filtersHeader";
import apiService from "../../../utils/api";
import CircularProgress from "@mui/material/CircularProgress";

export default function Categories() {
  // State
  const [tableData, setTableData] = React.useState([]);
  const [columns, setColumns] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  // Hooks
  const navigate = useNavigate();

  // Effects
  useEffect(() => {
    const authToken = sessionStorage.getItem("authToken");
    const tableColumns = ["foodItemName", "foodItemCategory", "foodItemAllergens", "foodItemIngredients", "foodItemEnergyKj", "foodItemEnergyKcal", "foodItemFat", "foodItemSaturates", "foodItemCarbohydrates", "foodItemSugars", "foodItemFibre", "foodItemProtein", "foodItemSalt", "foodItemComment", "foodItemId", "foodItemPhoto", "active"];
    const columnHeaders = ["Food Item", "Category", "Allergens", "Ingredients", "Comment", "Active"];
    const showHeaders = ["Food Item", "Category", "Allergens", "Ingredients", "Comment", "Active"];

    const displayNamesToAccessors = {
      "Food Item": "foodItemName",
      "Category": "categories",
      "Allergens": "allergens",
      "Ingredients": "foodItemIngredients",
      "Comment": "foodItemComment",
      "Active": "active"
    };

    if (authToken) {
      setLoading(true); // Start loading
      apiService
        .getFoodItems(authToken)
        .then((response) => {
          const getCategoryPromises = response.data.map((item) => apiService.getCategories(authToken));
          const getAllergenPromises = response.data.map((item) => apiService.getAllergens(authToken));
          // Create the data state object by mapping the desired keys

          Promise.all([...getCategoryPromises, ...getAllergenPromises])
          .then((responses) => {
            const categoryResponses = responses.slice(0, getCategoryPromises.length); // Extract category responses
            const allergenResponses = responses.slice(getCategoryPromises.length); // Extract allergen responses
            

          const filteredData = response.data.map((item, index) => {
            const categories = item.foodItemCategory.map((categoryId) =>
              categoryResponses[index].data.find((category) => category.categoryId === categoryId).category
            );

            const allergens = item.foodItemAllergens.map((allergenId) =>
              allergenResponses[index].data.find((allergen) => allergen.allergenId === allergenId).allergenName
            );

            const filteredItem = {};
            tableColumns.forEach((key) => {
              if (key === "foodItemId") {
                filteredItem["id"] = item[key];
              } else if (key === "foodItemCategory") {
                filteredItem["categories"] = categories.join(", ");
                filteredItem[key] = item[key];
              } else if (key === "foodItemAllergens") {
                filteredItem["allergens"] = allergens.join(", ");
                filteredItem[key] = item[key];
              } else if (key === "active") {
                filteredItem["active"] = (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={item[key] || false}
                        onChange={() => handleToggleActive(item.foodItemId)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                );
              }
              else {
                filteredItem[key] = item[key];
              }
            });
            return filteredItem;
          });
          setTableData(filteredData);

          setColumns(
            columnHeaders.map((key) => {
              return {
                header: key,
                accessor: displayNamesToAccessors[key],
                show: showHeaders.includes(key)
              };
            })
          );

        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setLoading(false); // Stop loading after data processing
        });
      });
    } else {
      navigate("/");
    }
  }, [navigate]);

  const handleEdit = (id) => {
    // Handle edit action for the given ID
    const recordToEdit = tableData.find((item) => item.id === id);
    
    // Create a new object without React elements
    const serializedRecord = {...recordToEdit};
    
    // Remove the React element from the active property
    if (serializedRecord.active && typeof serializedRecord.active !== 'boolean') {
      // Store the boolean value instead of the React element
      const originalItem = tableData.find(item => item.id === id);
      // Try to extract the checked value from the original data if possible
      if (originalItem && originalItem.active && originalItem.active.props && 
          originalItem.active.props.control && 
          originalItem.active.props.control.props) {
        serializedRecord.active = originalItem.active.props.control.props.checked;
      } else {
        // Fallback to a default value if we can't extract it
        serializedRecord.active = true;
      }
    }
    
    navigate(`/fooditems/edit/${id}`, { state: { record: serializedRecord } });
  };

  const handleDelete = async (id) => {
    // Handle delete action for the given ID
    try {
      const authToken = sessionStorage.getItem("authToken");

      if (authToken) {
          const response = await apiService.deleteFoodItem(authToken, id);

          // Fetch updated data after deletion
          const updatedData = await apiService.getFoodItems(authToken);
          setTableData(updatedData.data);
      }
  } catch (error) {
      console.error("Error deleting:", error);
  }
  };

  const handleToggleActive = async (id) => {
    try {
      const authToken = sessionStorage.getItem("authToken");
      if (authToken) {
        // Call the API to toggle active status
        const response = await apiService.toggleFoodItemActive(authToken, id);
        
        // Use the API response to update just the toggled item in our state
        setTableData(prevData => 
          prevData.map(item => {
            // If this is the item we just toggled
            if (item.id === response.data.id) {
              return {
                ...item,
                active: (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={response.data.active}
                        onChange={() => handleToggleActive(item.id)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                )
              };
            }
            return item;
          })
        );
      }
    } catch (error) {
      console.error("Error toggling active status:", error);
    }
  };

  return (
    <Container>
      <ViewHeader screenName="Food Items" />
      <FiltersHeader add="Add a Food Item" />
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      ) : (
        <SimpleTable
          data={tableData}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </Container>
  );
}
